import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, X } from "lucide-react";

const inputCls = "w-full h-10 px-3 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500";
const lbl = "text-[11px] font-semibold uppercase tracking-widest text-slate-600";

const EMPTY_CUST = {
  name: "", mobile: "", email: "", address: "", postal_address: "",
  license_number: "", license_version: "", gender: "",
};

export default function MarkSoldModal({ vehicle, onClose, onDone }) {
  const [mode, setMode] = useState("new"); // "new" or "existing"
  const [customers, setCustomers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [newCustomer, setNewCustomer] = useState(EMPTY_CUST);
  const [sale, setSale] = useState({
    sale_price: vehicle?.list_price || 0,
    sale_date: new Date().toISOString().slice(0, 10),
    payment_method: "Bank Transfer",
    notes: "",
  });

  useEffect(() => { api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!sale.sale_price) return toast.error("Enter sale price");
    if (mode === "existing" && !customerId) return toast.error("Select an existing customer");
    if (mode === "new" && !newCustomer.name.trim()) return toast.error("Enter customer name");
    setSaving(true);
    try {
      const payload = {
        sale_price: +sale.sale_price,
        sale_date: new Date(sale.sale_date).toISOString(),
        payment_method: sale.payment_method,
        notes: sale.notes,
      };
      if (mode === "existing") payload.customer_id = customerId;
      else payload.new_customer = newCustomer;
      await api.post(`/vehicles/${vehicle.id}/sell`, payload);
      toast.success(`${vehicle.stock_no} marked as sold`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to record sale"); }
    setSaving(false);
  };

  const upd = (k, v) => setNewCustomer((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Mark as Sold</h3>
              <p className="text-xs text-slate-500 mt-0.5 font-mono">{vehicle.stock_no} · {vehicle.year} {vehicle.make} {vehicle.model}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Mode toggle */}
          <div className="grid grid-cols-2 gap-2 bg-slate-100 p-1 rounded-lg">
            <button data-testid="mode-new-customer" onClick={() => setMode("new")} className={`h-9 text-sm font-semibold rounded-md transition ${mode === "new" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>New buyer</button>
            <button data-testid="mode-existing-customer" onClick={() => setMode("existing")} className={`h-9 text-sm font-semibold rounded-md transition ${mode === "existing" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>Existing customer</button>
          </div>

          {mode === "existing" ? (
            <div>
              <label className={lbl}>Select buyer</label>
              <select data-testid="sell-customer-select" value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={inputCls + " mt-1.5"}>
                <option value="">Choose customer…</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.mobile || c.phone || c.email}</option>)}
              </select>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 -mb-1">Buyer details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className={lbl}>Full name *</label>
                  <input data-testid="new-customer-name" value={newCustomer.name} onChange={(e) => upd("name", e.target.value)} className={inputCls + " mt-1.5"} />
                </div>
                <div>
                  <label className={lbl}>Mobile</label>
                  <input data-testid="new-customer-mobile" value={newCustomer.mobile} onChange={(e) => upd("mobile", e.target.value)} className={inputCls + " mt-1.5 font-mono"} placeholder="+64 21 …" />
                </div>
                <div>
                  <label className={lbl}>Email</label>
                  <input data-testid="new-customer-email" value={newCustomer.email} onChange={(e) => upd("email", e.target.value)} className={inputCls + " mt-1.5"} type="email" />
                </div>
                <div className="sm:col-span-2">
                  <label className={lbl}>Physical address</label>
                  <input data-testid="new-customer-address" value={newCustomer.address} onChange={(e) => upd("address", e.target.value)} className={inputCls + " mt-1.5"} placeholder="Street, suburb, city" />
                </div>
                <div className="sm:col-span-2">
                  <label className={lbl}>Postal address</label>
                  <input data-testid="new-customer-postal" value={newCustomer.postal_address} onChange={(e) => upd("postal_address", e.target.value)} className={inputCls + " mt-1.5"} placeholder="If different from physical" />
                </div>
                <div>
                  <label className={lbl}>Driver Licence #</label>
                  <input data-testid="new-customer-license" value={newCustomer.license_number} onChange={(e) => upd("license_number", e.target.value.toUpperCase())} className={inputCls + " mt-1.5 font-mono"} />
                </div>
                <div>
                  <label className={lbl}>Licence version</label>
                  <input data-testid="new-customer-license-version" value={newCustomer.license_version} onChange={(e) => upd("license_version", e.target.value)} className={inputCls + " mt-1.5 font-mono"} placeholder="e.g. 384" />
                </div>
                <div className="sm:col-span-2">
                  <label className={lbl}>Gender</label>
                  <select data-testid="new-customer-gender" value={newCustomer.gender} onChange={(e) => upd("gender", e.target.value)} className={inputCls + " mt-1.5"}>
                    <option value="">Prefer not to say</option>
                    <option>Male</option><option>Female</option><option>Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Sale details */}
          <div className="pt-3 border-t border-slate-200">
            <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Sale details</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Sale price (NZD) *</label>
                <input data-testid="sell-price-input" type="number" value={sale.sale_price} onChange={(e) => setSale({ ...sale, sale_price: e.target.value })} className={inputCls + " mt-1.5 font-mono font-semibold"} />
              </div>
              <div>
                <label className={lbl}>Sale date</label>
                <input type="date" value={sale.sale_date} onChange={(e) => setSale({ ...sale, sale_date: e.target.value })} className={inputCls + " mt-1.5"} />
              </div>
              <div className="col-span-2">
                <label className={lbl}>Payment method</label>
                <select value={sale.payment_method} onChange={(e) => setSale({ ...sale, payment_method: e.target.value })} className={inputCls + " mt-1.5"}>
                  <option>Bank Transfer</option><option>Finance</option><option>Cash</option><option>EFTPOS</option><option>Trade-in + Cash</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className={lbl}>Notes</label>
                <textarea value={sale.notes} onChange={(e) => setSale({ ...sale, notes: e.target.value })} rows={2} className={inputCls + " mt-1.5 h-auto py-2"} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-6 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="h-10 px-4 border border-slate-300 bg-white text-slate-700 rounded-lg text-sm font-semibold">Cancel</button>
          <button onClick={submit} disabled={saving} data-testid="sell-confirm-button" className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> {saving ? "Recording…" : "Confirm Sale"}
          </button>
        </div>
      </div>
    </div>
  );
}
